import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useTenantKey } from './useTenantKey';

export interface PdfPageImage {
  /** Ready for an `<img>`; null while the page loads, and when it could not be loaded. */
  src: string | null;
  /** The page could not be loaded - as opposed to being on its way (again). */
  failed: boolean;
  /** Ask for the page once more. The same function from one render to the next. */
  retry: () => void;
}

/**
 * The cache key of one page's picture: one entry per workspace AND per page. Without the page in
 * it every page of every PDF would share one picture.
 */
export function pdfPageQueryKey(tenantKey: string, pageUrl: string | null) {
  return ['pdf-page', tenantKey, pageUrl] as const;
}

/** Whether there is a page to fetch yet: a page to ask for, and a workspace to ask as. */
export function pdfPageWanted(tenantKey: string, pageUrl: string | null): boolean {
  return Boolean(pageUrl) && tenantKey !== '';
}

/**
 * How one page's picture is asked for and held - everything the hook hands the query except the
 * fetch itself, in one place so that it can be pinned without a browser.
 */
export function pdfPageQuery(tenantKey: string, pageUrl: string | null) {
  return {
    queryKey: pdfPageQueryKey(tenantKey, pageUrl),
    enabled: pdfPageWanted(tenantKey, pageUrl),
    // The proxy itself allows five minutes of private caching; match it.
    staleTime: 5 * 60 * 1000,
    // Held for a minute after the page leaves the frame: paging back shows it at once, and leafing
    // through a long document does not pile every page up in memory.
    gcTime: 60 * 1000,
    // A page that cannot be had says so at once; the viewer decides when to ask again.
    retry: false,
    refetchOnWindowFocus: false,
  } as const;
}

/**
 * Whether a page is known not to be coming, as opposed to still being on its way. The fetch threw
 * (the server could not draw it just now), or answered that there is no such page - which arrives
 * as a null blob rather than an error. Not while it is being asked for again, and not while its
 * picture is in hand: a later ask that fails does not take away a page already shown.
 */
export function pdfPageFailed(state: {
  isError: boolean;
  isFetching: boolean;
  blob: Blob | null | undefined;
}): boolean {
  if (state.isFetching || state.blob) return false;
  return state.isError || state.blob === null;
}

/**
 * One page of a PDF as an `<img src>`, from the tenant-scoped `/api/assets/:id/pages/:n` proxy.
 *
 * Like every asset proxy it wants the bearer token, so a plain `<img>` cannot load it; this fetches
 * it as a blob. The blob is held in the query cache, so paging back to a page already seen shows it
 * at once rather than fetching it again - but only for a minute after the page leaves the frame, so
 * leafing through a long document does not pile every page up in memory.
 *
 * Unlike {@link useProxyImage}, a failure is told apart from "still loading": the first ask of a
 * page can take a moment while the server draws it, and a page that will never arrive must not sit
 * under a spinner. Pass null for no page.
 */
export function usePdfPage(pageUrl: string | null): PdfPageImage {
  const tenantKey = useTenantKey();
  const {
    data: blob,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    ...pdfPageQuery(tenantKey, pageUrl),
    queryFn: ({ signal }) => api.fetchAssetBlob(pageUrl as string, signal),
  });

  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setSrc(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // Stable, so that a viewer waiting to ask again is not set back by every render around it.
  const retry = useCallback(() => void refetch(), [refetch]);

  return { src, failed: pdfPageFailed({ isError, isFetching, blob }), retry };
}
